const state = {
  krpanoHooks: null,
  krpanoService: null,
  buildingId: '',
  user: {
    uid: '',
    channelId: '',
    role: ''
  },
  agentInfo: {},
  markerState: {
    id: '',
    isHover: false,
    isModalOpen: false,
    vwPercentage: 0,
    vhPercentage: 0,
    index: 0
  },
  videoplayState: {
    markerId: '',
    isPaused: false,
    isMuted: false,
    time: 0,
    volume: 1,
    isFullscreen: false
  },
  roomVideoplayStates: [],
  panoramas: {
    list: [],
    currentIndex: 0
  },
  panoramaState: {
    v: 0,
    h: 0,
    fov: 0
  },
  panelState: {
    isNavCollapse: true,
    navHeight: 0,
    isOpenFloorPlan: false,
    isOpenLocation: false,
    isOpenModal: false,
    modalType: ''
  },
  audioPlayState: {
    markerId: '',
    isActive: false,
    isPaused: false,
    source: null,
    title: ''
  }
}

export const getters = {
  krpanoHooks: state => state.krpanoHooks,
  krpanoService: state => state.krpanoService,
  buildingId: state => state.buildingId,
  user: state => state.user,
  agentInfo: state => state.agentInfo,
  markerState: state => state.markerState,
  currentPanoramaIndex: state => state.panoramas.currentIndex,
  currentMarker: (state, getters) => {
    if (getters.panoramas.length === 0) return null
    if (!getters.markerState.id === '') return null
    const markers = getters.panoramas[getters.currentPanoramaIndex].markers
    return markers.find(marker => marker.objectId === getters.markerState.id)
  },
  currentPanorama: (state, getters) => {
    return state.panoramas.list[getters.currentPanoramaIndex]
  },
  panoramas: state => state.panoramas.list,
  isNavCollapse: state => state.panelState.isNavCollapse,
  bottomNavState: state => {
    return {
      isNavCollapse: state.panelState.isNavCollapse,
      navHeight: state.panelState.navHeight
    }
  },
  panoramaPreviews: state => {
    const previews = state.panoramas.list.map(panorama => {
      const { objectId, thumbnail, category, customCategory } = panorama

      return {
        id: objectId,
        url: thumbnail,
        text: category === 'custom' ? customCategory : category
      }
    })

    return previews
  },
  isAgent: state => state.user.role === 'agent',
  panelState: state => state.panelState,
  panoramaState: state => state.panoramaState,
  videoplayState: state => state.videoplayState,
  roomVideoplayStates: state => state.roomVideoplayStates,
  audioPlayState: state => state.audioPlayState
}

export const actions = {
  setKrpanoHooks ({ commit }, hooks) {
    commit('SET_KRPANO_HOOKS', hooks)
  },
  setKrpanoService ({ commit }, service) {
    commit('SET_KRPANO_SERVICE', service)
  },
  setBuildingId ({ commit }, id) {
    commit('SET_BUILDING_ID', id)
  },
  setAgentInfo ({ commit }, agentInfo = {}) {
    commit('SET_AGENT_INFO', agentInfo)
  },
  setUserInfo ({ commit }, data) {
    commit('SET_USER_INFO', data)
  },
  setUserRole ({ commit }, data) {
    commit('SET_USER_ROLE', data)
  },
  updateMarkerState ({ commit }, setting) {
    commit('UPDATE_MARKER_STATE', setting)
  },
  updatePanoramaList ({ commit }, panoramas) {
    commit('UPDATE_PANORAMA_LIST', panoramas)
  },
  updateCurrentPanoramaIndex ({ commit }, index) {
    commit('UPDATE_CURRENT_PANORAMA_INDEX', index)
  },
  toggleNavCollapse ({ commit }) {
    commit('TOGGLE_NAV_COLLAPSE')
  },
  setNavHeight ({ commit }, height) {
    commit('SET_NAV_HEIGHT', height)
  },
  updatePanelState ({ commit }, data) {
    commit('UPDATE_PANEL_STATE', data)
  },
  updatePanoramaState ({ commit }, data) {
    commit('UPDATE_PANORAMA_STATE', data)
  },
  updateVideoplayState ({ commit }, data) {
    commit('UPDATE_VIDEOPLAY_STATE', data)
  },
  updateAudioplayState ({ commit, state, rootState }, data) {
    if (data) {
      commit('UPDATE_AUDIOPLAY_STATE', data)
      return
    }
    if (state.audioPlayState.isActive) {
      const currentData = {
        currentTime: rootState.markers.musicPlayerRef.currentTime || 0
      }
      commit('UPDATE_AUDIOPLAY_STATE', currentData)
    }
  },
  async updateRoomVideoplayStates ({ commit, getters }, data) {
    if (data) {
      commit('UPDATE_ROOM_VIDEOPLAY_STATES', data)
      return
    }
    const { krpanoHooks, krpanoService, currentPanorama, videoplayState } = getters
    const videoMarkers = currentPanorama.markers.filter(marker => marker.type === 'videoplay')
    const videoMarkerStates = await Promise.all(videoMarkers.map(getVideoMarkerState))
    commit('UPDATE_ROOM_VIDEOPLAY_STATES', videoMarkerStates)
    async function getVideoMarkerState ({ objectId: markerId }) {
      const eventName = 'getVideopalyState'
      const isFullscreen = markerId === videoplayState.markerId ? videoplayState.isFullscreen : false
      const [isPaused, isMuted, time, volume] = await new Promise((resolve) => {
        krpanoHooks.one(eventName, (...args) => resolve(args))
        krpanoService.call(`
          set(isPaused, get(hotspot[marker_${markerId}].ispaused));
          set(isMuted, get(hotspot[marker_${markerId}].muted));
          set(time, get(hotspot[marker_${markerId}].time));
          set(volume, get(hotspot[marker_${markerId}].volume));
          jscall(calc(
            'krHooks.emit("${eventName}",'
            + isPaused + ', '
            + isMuted + ', '
            + time + ', '
            + volume + ')'
          ));`)
      })
      return { isPaused, isMuted, time, volume, markerId, isFullscreen }
    }
  }
}

export const mutations = {
  SET_KRPANO_HOOKS (state, hooks) {
    state.krpanoHooks = hooks
  },
  SET_KRPANO_SERVICE (state, service) {
    state.krpanoService = service
  },
  SET_BUILDING_ID (state, id) {
    state.buildingId = id
  },
  SET_USER_INFO (state, data) {
    state.user = { ...state.user, ...data }
  },
  SET_USER_ROLE (state, data) {
    state.user.role = data
  },
  SET_AGENT_INFO (state, agentInfo) {
    state.agentInfo = agentInfo
  },
  UPDATE_MARKER_STATE (state, data) {
    state.markerState = { ...state.markerState, ...data }
  },
  UPDATE_PANORAMA_LIST (state, panoramas) {
    state.panoramas.list = panoramas
  },
  UPDATE_CURRENT_PANORAMA_INDEX (state, index) {
    state.panoramas.currentIndex = index
  },
  TOGGLE_NAV_COLLAPSE (state) {
    state.panelState.isNavCollapse = !state.panelState.isNavCollapse
  },
  SET_NAV_HEIGHT (state, height) {
    state.panelState.navHeight = height
  },
  UPDATE_PANEL_STATE (state, data) {
    state.panelState = { ...state.panelState, ...data }
  },
  UPDATE_PANORAMA_STATE (state, data) {
    state.panoramaState = { ...state.panoramaState, ...data }
  },
  UPDATE_VIDEOPLAY_STATE (state, data) {
    state.videoplayState = { ...state.videoplayState, ...data }
  },
  UPDATE_ROOM_VIDEOPLAY_STATES (state, data) {
    state.roomVideoplayStates = data
  },
  UPDATE_AUDIOPLAY_STATE (state, data) {
    state.audioPlayState = { ...state.audioPlayState, ...data }
  }
}

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
}
